"use client";

import { CheckIcon } from "@heroicons/react/20/solid";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import { isToday, isTomorrow, startOfDay } from "date-fns";
import { FC, Fragment, useRef, useState } from "react";
import { twMerge } from "tailwind-merge";
import { toLocalDatetimeString } from "../utils/date";
import Button from "./Button";
import DatePicker from "./DatePicker";
import DropdownMenu from "./DropdownMenu";
import DropdownMenuDivider from "./DropdownMenuDivider";
import {
  DEFAULT_PREFERRED_HOURS,
  getPreferredHoursForCadence,
} from "./publicationFlow/engine/types";
import {
  formatScheduleShortDateLabel,
  formatScheduleTimeLabel,
} from "./ScheduleDateLabel";
import TimeSlotSelect, { formatSlotHourLabel } from "./TimeSlotSelect";

// Trigger label shows at most this many times; the rest collapse into +N.
const MAX_SUMMARY_TIMES = 2;

const INACTIVE_SLOT_TOOLTIP = "Not used by any post in the current queue";

const slotName = (index: number) => `Post ${index + 1}`;

// Relative-day vocabulary shared by the chip suffix and the Starts row: an
// absolute date for today ("Aug 31") makes the reader do the calendar math
// the label should do for them.
const dayToken = (date: Date): string => {
  if (isToday(date)) return "Today";
  if (isTomorrow(date)) return "Tomorrow";
  return formatScheduleShortDateLabel(date);
};

type SchedulePickerDropdownProps = {
  /**
   * Posting hours (0–23), one per daily slot, chronological — the source
   * (getPreferredHoursForCadence) sorts, so "Post k" is the k-th post of
   * the day and an account posting k times a day uses the first k rows.
   */
  hours: number[];
  /** Receives the full next hours array (a duplicate pick swaps two slots).
   *  Absent → the hour rows are hidden and the chip is start-only.
   *  Return `false` to close the panel (e.g. a re-plan confirm is opening). */
  onChangeHours?: (hours: number[]) => void | false;
  /**
   * Slot hours the current queue actually lands on (engine-resolved).
   * Hours outside this set render lighter as a "won't be used by this
   * batch" hint — e.g. 1 queued video on a 2/day cadence only fills one
   * slot. The hint only shows when it's informative (some visible hours
   * active, some not); rows stay fully editable either way.
   */
  activeHours?: ReadonlySet<number>;
  variant?: "tertiary" | "secondary";
  /** The distribution lower bound (config.startFrom). */
  startDate?: Date;
  /** Earliest engine-resolved slot — when posting actually begins. */
  effectiveStartDate?: Date;
  /** undefined = back to "as soon as possible".
   *  Return `false` to close the panel (e.g. a re-plan confirm is opening). */
  onStartDateChange: (date: Date | undefined) => void | false;
  /**
   * The chip renders in both the mobile and desktop header sections, but
   * the hidden calendar must anchor to a visible chip — an anchor inside a
   * display:none section has a collapsed rect. Only the active layout's
   * instance mounts the picker.
   */
  mountStartDatePicker?: boolean;
  /**
   * Stretch the chip to its container (the mobile header's rows, where
   * every control spans the row or splits it evenly with a sibling —
   * the root flexes from a zero basis so a chip beside it gets the same
   * share). Off, the chip hugs its label.
   */
  fullWidth?: boolean;
  /**
   * Hours the chip's summary spells out before folding the rest into
   * "+N". The desktop island shows two; the mobile row the chip shares
   * with the accounts chip shows one, so a 2/day cadence reads
   * "4 PM, +1" beside three avatars instead of truncating mid-hour.
   * The panel lists every slot regardless.
   */
  maxSummaryTimes?: number;
  /** Scope disclosures stay inside the corresponding settings panels. */
  hoursScopeHint?: string;
  startScopeHint?: string;
};

/**
 * The header's unified schedule picker: posting-time slots and the start
 * bound behind one trigger. The panel is a uniform property list — Post 1,
 * Post 2, Starts — where every row opens a flyout to its side. "As soon as
 * possible" is the default start: the engine books the next preferred hour
 * after now, so ASAP is simply a startFrom at/behind now, and picking a
 * date is a sticky override (hour changes never touch it). The Starts value
 * always shows the effective first day (Today/Tomorrow/date). The chip's
 * muted "Starting …" suffix appears ONLY for an explicit date pick: in ASAP
 * mode the start is the engine's result, not a setting, and the queue's
 * first day strip (with its today dot) already names it — so the chip
 * stays a pure settings chip until the user makes the start a choice.
 * ASAP-vs-custom also shows as the flyout's check.
 */
const SchedulePickerDropdown: FC<SchedulePickerDropdownProps> = ({
  hours,
  onChangeHours,
  activeHours,
  variant = "tertiary",
  startDate,
  effectiveStartDate,
  onStartDateChange,
  mountStartDatePicker = true,
  fullWidth = false,
  maxSummaryTimes = MAX_SUMMARY_TIMES,
  hoursScopeHint,
  startScopeHint,
}) => {
  const startDatePickerRef = useRef<any>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isStartFlyoutOpen, setIsStartFlyoutOpen] = useState(false);

  // ASAP = no explicit future bound. A past-or-now startFrom is exactly
  // what the engine's max(startFrom, now) clamp treats as "from now", so
  // no sentinel is needed to represent the default mode.
  const isAsapStart = !startDate || startDate.getTime() <= new Date().getTime();

  // A midnight bound comes from a date-only pick ("start from this day"),
  // so show just the day; an explicit time pick appends the time.
  const isStartFromMidnight =
    !!startDate && startDate.getHours() === 0 && startDate.getMinutes() === 0;
  const customStartValueLabel =
    !isAsapStart && startDate
      ? isStartFromMidnight
        ? dayToken(startDate)
        : `${dayToken(startDate)} ${formatScheduleTimeLabel(startDate)}`
      : null;
  // In ASAP mode the value is the effective first slot's day. The engine
  // never books an elapsed hour, so today's passed hours roll the first
  // post to a later day and the value must not imply "today". A past
  // effective date (batch edits holding published posts) clamps to Today.
  const asapStartValueLabel = (() => {
    if (!effectiveStartDate) return "Today";
    if (startOfDay(effectiveStartDate) <= startOfDay(new Date())) {
      return "Today";
    }
    return dayToken(effectiveStartDate);
  })();
  const startValueLabel = customStartValueLabel ?? asapStartValueLabel;

  const shownHours = hours.slice(0, Math.max(1, maxSummaryTimes));
  const hiddenCount = hours.length - shownHours.length;
  const hasHourRows = Boolean(onChangeHours) && hours.length > 0;
  const recommendedHours = getPreferredHoursForCadence(
    DEFAULT_PREFERRED_HOURS,
    hours.length,
  );
  const sharedPeriod = hours.every((hour) => (hour < 12) === (hours[0] < 12));
  const summaryHourLabel = (hour: number, index: number) => {
    const label = formatSlotHourLabel(hour).replace(":00", "");
    return sharedPeriod && index < shownHours.length - 1
      ? label.replace(/ (AM|PM)$/, "")
      : label;
  };

  const closeAll = () => {
    setIsStartFlyoutOpen(false);
    setIsOpen(false);
  };

  const commitHour = (index: number, hour: number) => {
    if (!onChangeHours) return;
    const next = [...hours];
    const otherIndex = hours.findIndex((h, i) => i !== index && h === hour);
    if (otherIndex !== -1) next[otherIndex] = hours[index];
    next[index] = hour;
    if (onChangeHours(next) === false) closeAll();
  };

  // Only hint when it separates the hours: at least one visible hour used
  // and one unused. All-active (full queue) and all-inactive (e.g. every
  // post on a custom datetime) both render at full strength.
  const showActiveHint =
    activeHours != null &&
    hours.some((hour) => activeHours.has(hour)) &&
    hours.some((hour) => !activeHours.has(hour));
  const isInactiveHour = (hour: number) =>
    showActiveHint && !activeHours!.has(hour);

  // The panel must be gone before the calendar opens: the datepicker
  // popper (!z-[99999]) sits below the popover panel, and its clicks would
  // register as outside-clicks on a still-open panel. Close now, open on
  // the next macrotask once the panel has unmounted.
  const handlePickStartDate = () => {
    closeAll();
    setTimeout(() => {
      startDatePickerRef.current?.focus();
      startDatePickerRef.current?.showPicker();
    }, 0);
  };

  // Keeps the panel open so the Starts value is seen flipping back —
  // unless the change opens a re-plan confirm, which takes the screen.
  const handleAsapStart = () => {
    setIsStartFlyoutOpen(false);
    if (!isAsapStart && onStartDateChange(undefined) === false) closeAll();
  };

  return (
    <div
      className={twMerge(
        "relative flex items-center min-w-0",
        fullWidth && "flex-1 w-full",
      )}
    >
      {mountStartDatePicker ? (
        <div className="absolute inset-0 overflow-visible pointer-events-none">
          <DatePicker
            className="absolute inset-0"
            ref={startDatePickerRef}
            value={startDate ? toLocalDatetimeString(startDate) : ""}
            portalId="react-datepicker-popper-root"
            popperStrategy="fixed"
            // The date acts as a distribution lower bound, so a bare date
            // click must keep the whole day available (00:00), not jump to
            // a preferred hour and exclude earlier slots.
            dateOnlyDefaultTime="startOfDay"
            onChange={(date) => {
              if (onStartDateChange(date) === false) closeAll();
            }}
          />
        </div>
      ) : null}
      <DropdownMenu
        open={isOpen}
        onOpenChange={(nextOpen) => {
          setIsOpen(nextOpen);
          if (!nextOpen) setIsStartFlyoutOpen(false);
        }}
        size="m"
        positions={["bottom"]}
        align="center"
      >
        <DropdownMenu.Trigger>
          <Button
            type="button"
            variant={variant}
            size="m"
            // align="start" for the trailing chevron's sake: it is what
            // gives a select's caret its 36px slot (and the pr-0 that goes
            // with it), so this chip's caret sits on the same 10px inset as
            // the shorts and accounts chips beside it instead of 8/13.
            align="start"
            className={twMerge("min-w-0 max-w-full truncate", fullWidth && "w-full")}
            aria-label="Posting schedule"
            data-tooltip-id="global"
            data-tooltip-content="Posting schedule"
          >
            <Button.Text>
              {hasHourRows ? (
                <>
                  {shownHours.map((hour, index) => (
                    <Fragment key={index}>
                      {index > 0 ? ", " : null}
                      {isInactiveHour(hour) ? (
                        <span
                          className="text-slate-400"
                          data-tooltip-id="global"
                          data-tooltip-content={INACTIVE_SLOT_TOOLTIP}
                        >
                          {summaryHourLabel(hour, index)}
                        </span>
                      ) : (
                        summaryHourLabel(hour, index)
                      )}
                    </Fragment>
                  ))}
                  {hiddenCount > 0 ? `, +${hiddenCount}` : null}
                  {/* A muted start bound, separated by spacing alone.
                      Explicit picks only — see the component comment. */}
                  {!isAsapStart ? (
                    <span className="ml-1 text-slate-500">
                      {" "}
                      Starting {startValueLabel}
                    </span>
                  ) : null}
                </>
              ) : (
                `Starting ${startValueLabel}`
              )}
            </Button.Text>
            <ChevronDownIcon />
          </Button>
        </DropdownMenu.Trigger>
        <DropdownMenu.Content>
          <ul className="min-w-52">
            {hasHourRows && hoursScopeHint ? <li className="max-w-64 px-3 pt-2 pb-1 text-xs text-slate-500">{hoursScopeHint}</li> : null}
            {hasHourRows
              ? hours.map((hour, index) => (
                  <li key={index}>
                    <TimeSlotSelect
                      value={hour}
                      recommendedHour={recommendedHours[index]}
                      ariaLabel={`Posting time ${index + 1}`}
                      onChange={(nextHour) => commitHour(index, nextHour)}
                      positions={["right", "left", "bottom"]}
                      align="start"
                      takenHours={
                        new Set(
                          hours.filter(
                            (_, siblingIndex) => siblingIndex !== index,
                          ),
                        )
                      }
                      trigger={
                        <Button
                          type="button"
                          size="m"
                          variant="tertiary"
                          align="start"
                          className="w-full"
                          data-tooltip-id={
                            isInactiveHour(hour) ? "global" : undefined
                          }
                          data-tooltip-content={
                            isInactiveHour(hour)
                              ? INACTIVE_SLOT_TOOLTIP
                              : undefined
                          }
                        >
                          <Button.Text>
                            <span
                              className={
                                isInactiveHour(hour)
                                  ? "text-slate-400 font-normal"
                                  : "text-slate-500 font-normal"
                              }
                            >
                              {hours.length === 1
                                ? "Posting time"
                                : slotName(index)}
                            </span>
                          </Button.Text>
                          <Button.Content>
                            <span
                              className={`ml-auto flex items-center gap-1 pl-6 whitespace-nowrap text-sm font-medium font-display ${
                                isInactiveHour(hour) ? "text-slate-400" : ""
                              }`}
                            >
                              {formatSlotHourLabel(hour)}
                              <ChevronDownIcon className="w-4 h-4 text-slate-500 shrink-0" />
                            </span>
                          </Button.Content>
                        </Button>
                      }
                    />
                  </li>
                ))
              : null}
            {hasHourRows ? (
              <li>
                <DropdownMenuDivider />
              </li>
            ) : null}
            <li>
              <DropdownMenu
                open={isStartFlyoutOpen}
                onOpenChange={setIsStartFlyoutOpen}
                size="m"
                positions={["right", "left", "bottom"]}
                align="start"
              >
                <DropdownMenu.Trigger>
                  <Button
                    type="button"
                    size="m"
                    variant="tertiary"
                    align="start"
                    className="w-full"
                  >
                    <Button.Text>
                      <span className="text-slate-500 font-normal">Starts</span>
                    </Button.Text>
                    <Button.Content>
                      <span className="ml-auto flex items-center gap-1 pl-6 whitespace-nowrap text-sm font-medium font-display">
                        {startValueLabel}
                        <ChevronDownIcon className="w-4 h-4 text-slate-500 shrink-0" />
                      </span>
                    </Button.Content>
                  </Button>
                </DropdownMenu.Trigger>
                <DropdownMenu.Content>
                  <ul>
                    {startScopeHint ? <li className="max-w-56 px-3 pt-2 pb-1 text-xs text-slate-500">{startScopeHint}</li> : null}
                    <li>
                      <Button
                        type="button"
                        size="m"
                        variant="tertiary"
                        align="start"
                        className="w-full"
                        onClick={handleAsapStart}
                      >
                        As soon as possible
                        {isAsapStart ? <CheckIcon /> : null}
                      </Button>
                    </li>
                    <li>
                      <Button
                        type="button"
                        size="m"
                        variant="tertiary"
                        align="start"
                        className="w-full"
                        onClick={handlePickStartDate}
                      >
                        On a specific date…
                        {!isAsapStart ? <CheckIcon /> : null}
                      </Button>
                    </li>
                  </ul>
                </DropdownMenu.Content>
              </DropdownMenu>
            </li>
          </ul>
        </DropdownMenu.Content>
      </DropdownMenu>
    </div>
  );
};

export default SchedulePickerDropdown;
